import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useRoamZoneGate } from "@/hooks/useRoamZoneGate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_COLORS = {
  prologue:  { border: "#A78BFA", glow: "rgba(167,139,250,0.4)" },
  early:     { border: "#34D399", glow: "rgba(52,211,153,0.4)" },
  "early-mid": { border: "#FBBF24", glow: "rgba(251,191,36,0.4)" },
  mid:       { border: "#F97316", glow: "rgba(249,115,22,0.45)" },
  endgame:   { border: "#EF4444", glow: "rgba(239,68,68,0.55)" },
  // 🌊 Safe zones — no combat, no gates. Rendered with a soft cyan glow
  // so travellers can spot rest stops at a glance (owner canon Mar 5, 2026).
  safe_zone: { border: "#22D3EE", glow: "rgba(34,211,238,0.45)" },
};

export default function WorldMap() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();
  const roamGate = useRoamZoneGate();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/world-map/regions`, { withCredentials: true });
        setData(res.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load world map");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return <div className="p-8 text-center text-zinc-400" data-testid="worldmap-loading">Loading the world…</div>;
  }
  if (!data) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center text-zinc-300" data-testid="worldmap-empty">
        <div className="text-6xl mb-3">🗺️</div>
        <h2 className="text-xl font-bold">World atlas unavailable.</h2>
        <p className="text-zinc-500 text-sm mt-2">Sign in to view the map.</p>
      </div>
    );
  }

  const { grid, regions, unlocked_count, total_count } = data;
  const { rows, cols } = grid;

  // Build a map-position lookup
  const at = {};
  regions.forEach(r => { at[`${r.row}-${r.col}`] = r; });

  return (
    <div className="worldmap max-w-7xl mx-auto p-4 sm:p-6 text-white"
         data-testid="worldmap"
         style={{
           minHeight: "100vh",
           background: "radial-gradient(ellipse at center, #1E293B 0%, #0F172A 60%, #020617 100%)",
         }}>
      <div className="text-center mb-6">
        <div className="text-5xl mb-2">🗺️</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-wider"
            data-testid="worldmap-title">
          World of Omnimuscle
        </h1>
        <p className="text-zinc-400 text-sm mt-2">
          Click a revealed region to read the canon. Locked regions appear when you progress.
        </p>
        <div className="mt-3 inline-flex items-center gap-2 text-xs text-amber-300/80 bg-amber-400/10 border border-amber-400/30 rounded-full px-3 py-1"
             data-testid="worldmap-progress">
          <span>🧭</span>
          <span className="font-bold tabular-nums">{unlocked_count} / {total_count}</span>
          <span className="opacity-70">regions revealed</span>
        </div>
      </div>

      {/* 🌾 Castle Courtyard callout — the roaming zone. Gated on Scene I. */}
      <button
        type="button"
        onClick={() => roamGate.unlocked && nav("/roam")}
        disabled={!roamGate.unlocked}
        data-testid="worldmap-roam-callout"
        className={`mx-auto mb-4 flex items-center gap-3 max-w-2xl w-full text-left rounded-lg px-4 py-3 border transition-colors ${
          roamGate.unlocked
            ? "bg-amber-950/30 border-amber-500/50 hover:border-amber-300 text-amber-100"
            : "bg-neutral-950/60 border-neutral-800 text-neutral-500 cursor-not-allowed"
        }`}
      >
        <span className="text-2xl">🌾</span>
        <span className="flex-1 min-w-0">
          <span className="block text-[10px] uppercase tracking-widest opacity-70">Central Square</span>
          <span className="block font-bold truncate">Castle Courtyard · The Fountain</span>
          <span className="block text-xs opacity-70 truncate">
            {roamGate.unlocked
              ? "Grandma Cari is waiting by the fountain."
              : `🔒 ${roamGate.reason}`}
          </span>
        </span>
        {roamGate.unlocked && <span className="text-lg opacity-80">→</span>}
      </button>

      {/* The Map Grid — N at top, S at bottom */}
      <div className="relative mx-auto"
           style={{
             aspectRatio: `${cols}/${rows}`,
             maxWidth: 1100,
             background:
               "linear-gradient(135deg, rgba(30,41,59,0.6) 0%, rgba(15,23,42,0.8) 100%)," +
               " repeating-linear-gradient(0deg, transparent, transparent 24px, rgba(255,255,255,0.02) 25px)," +
               " repeating-linear-gradient(90deg, transparent, transparent 24px, rgba(255,255,255,0.02) 25px)",
             border: "1px solid rgba(255,255,255,0.08)",
             borderRadius: 14,
             padding: 18,
           }}
           data-testid="worldmap-grid">
        {/* Compass */}
        <div className="absolute top-3 right-3 text-xs uppercase tracking-widest text-zinc-500 font-bold">
          <div className="text-center">N</div>
          <div className="flex items-center gap-3">
            <span>W</span>
            <div className="w-6 h-6 border border-zinc-500 rounded-full grid place-items-center">+</div>
            <span>E</span>
          </div>
          <div className="text-center">S</div>
        </div>

        {/* Tiles */}
        <div className="grid gap-2 sm:gap-3 w-full h-full relative"
             style={{
               gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
               gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
             }}>
          {/* 🌊 Water stream — Glass-Spring Oasis (S, r5·c2) → Western
              Gateway (W, r2·c1). A soft cyan ribbon that curves up the
              canyons so the Southern↔Western passage feels traversable.
              SVG sits behind tiles via z-0 so the stream never eats clicks.
              Owner canon Mar 5, 2026. */}
          <svg
            className="absolute inset-0 pointer-events-none"
            style={{ width: "100%", height: "100%", zIndex: 0 }}
            viewBox={`0 0 ${cols * 100} ${rows * 100}`}
            preserveAspectRatio="none"
            data-testid="worldmap-water-stream"
          >
            <defs>
              <linearGradient id="stream-flow" x1="0%" y1="100%" x2="0%" y2="0%">
                <stop offset="0%"   stopColor="#22D3EE" stopOpacity="0.85" />
                <stop offset="55%"  stopColor="#38BDF8" stopOpacity="0.75" />
                <stop offset="100%" stopColor="#0EA5E9" stopOpacity="0.60" />
              </linearGradient>
              <filter id="stream-glow">
                <feGaussianBlur stdDeviation="1.8" result="b" />
                <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            {/* Path: from Oasis center (250, 550) up through 200,450 → 150,300 → 130,200 → Western Gateway center (150, 250). */}
            <path
              d="M 250 555 C 220 500, 200 460, 180 400 S 130 300, 150 250"
              fill="none"
              stroke="url(#stream-flow)"
              strokeWidth="6"
              strokeLinecap="round"
              filter="url(#stream-glow)"
            />
            {/* Subtle ripple twin — creates a shimmer without adding motion. */}
            <path
              d="M 250 555 C 220 500, 200 460, 180 400 S 130 300, 150 250"
              fill="none"
              stroke="#CFFAFE"
              strokeWidth="1.4"
              strokeLinecap="round"
              opacity="0.55"
            />
          </svg>
          {Array.from({ length: rows * cols }, (_, idx) => {
            const r = Math.floor(idx / cols);
            const c = idx % cols;
            const region = at[`${r}-${c}`];
            if (!region) {
              return <div key={idx} className="opacity-0" />;
            }
            return (
              <div key={region.id} className="relative z-10">
                <RegionTile
                  region={region}
                  onClick={() => setSelected(region)} />
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Legend ── */}
      <div className="mt-6 flex flex-wrap gap-3 justify-center text-xs">
        {Object.entries(TIER_COLORS).map(([tier, sty]) => (
          <div key={tier}
               className="flex items-center gap-1.5 px-2 py-1 rounded border"
               style={{ borderColor: sty.border, color: sty.border }}>
            <div className="w-2 h-2 rounded-full"
                 style={{ background: sty.border, boxShadow: `0 0 6px ${sty.glow}` }} />
            <span className="uppercase tracking-wider font-bold">{tier}</span>
          </div>
        ))}
      </div>

      {/* ── Region Detail Modal ── */}
      {selected && (
        <RegionModal region={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function RegionTile({ region, onClick }) {
  const sty = TIER_COLORS[region.tier] || TIER_COLORS.early;
  const isLocked = !region.unlocked;
  const isTbd = (region.canon_status === "tbd");

  return (
    <button
      onClick={onClick}
      data-testid={`worldmap-region-${region.id}`}
      className="relative rounded-lg border-2 transition-all flex flex-col items-center justify-center p-1 sm:p-2 group"
      style={{
        borderColor: isLocked ? "#3F3F46" : sty.border,
        background: isLocked
          ? "rgba(24,24,27,0.85)"
          : `linear-gradient(135deg, rgba(0,0,0,0.5), ${sty.glow})`,
        filter: isLocked ? "grayscale(95%) brightness(0.6)" : "none",
        boxShadow: isLocked ? "none" : `0 0 18px ${sty.glow}`,
        cursor: "pointer",
      }}
    >
      <div className="text-2xl sm:text-3xl mb-0.5"
           style={{ opacity: isLocked ? 0.4 : 1 }}>
        {isLocked ? (isTbd ? "❓" : "🔒") : region.icon}
      </div>
      <div className="text-[9px] sm:text-[10px] font-bold text-center leading-tight"
           style={{ color: isLocked ? "#71717A" : "#F8FAFC" }}>
        {region.name_display}
      </div>
      {!isLocked && region.level_range && (
        <div className="text-[8px] opacity-60 mt-0.5" style={{ color: sty.border }}>
          Lv {region.level_range[0]}-{region.level_range[1]}
        </div>
      )}
      {isLocked && !isTbd && (
        <div className="text-[8px] text-zinc-500 mt-0.5 text-center leading-none">
          {region.unlock_hint}
        </div>
      )}
      {isTbd && (
        <div className="text-[8px] text-amber-500/70 mt-0.5 font-bold tracking-wider uppercase">
          Coming Soon
        </div>
      )}
    </button>
  );
}

function RegionModal({ region, onClose }) {
  const sty = TIER_COLORS[region.tier] || TIER_COLORS.early;
  const isLocked = !region.unlocked;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
         onClick={onClose}
         data-testid="worldmap-region-modal">
      <div className="max-w-lg w-full rounded-xl p-6 border-2"
           onClick={(e) => e.stopPropagation()}
           style={{
             background: "linear-gradient(135deg, #0F172A, #1E293B)",
             borderColor: sty.border,
             boxShadow: `0 0 40px ${sty.glow}`,
             color: "#F8FAFC",
             filter: isLocked ? "grayscale(70%)" : "none",
           }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-4xl mb-1">{isLocked ? "🔒" : region.icon}</div>
            <h2 className="text-2xl font-bold tracking-wide"
                data-testid="worldmap-modal-title">
              {region.name_display}
            </h2>
            {!isLocked && region.level_range && (
              <div className="text-xs mt-1"
                   style={{ color: sty.border }}>
                Tier: <span className="uppercase font-bold">{region.tier}</span>
                {" · "}
                Lv {region.level_range[0]}–{region.level_range[1]}
              </div>
            )}
          </div>
          <button onClick={onClose}
                  data-testid="worldmap-modal-close"
                  className="text-2xl text-zinc-500 hover:text-white transition">
            ×
          </button>
        </div>

        <div className="mt-4 text-sm leading-relaxed whitespace-pre-line"
             style={{ color: isLocked ? "#71717A" : "#E2E8F0" }}
             data-testid="worldmap-modal-blurb">
          {region.blurb}
        </div>

        {isLocked && (
          <div className="mt-4 p-3 rounded border border-zinc-700 bg-zinc-900/60 text-xs">
            <div className="text-amber-300 font-bold mb-1">🔒 Locked</div>
            <div className="text-zinc-400">{region.unlock_hint}</div>
            {region.canon_status === "tbd" && (
              <div className="text-zinc-600 mt-1 text-[10px]">
                Canon for this region is pending. Drop your spec in chat to unlock it.
              </div>
            )}
          </div>
        )}

        {/* 🎣 Enter-Region CTA — currently only Glass-Spring Oasis has a
            walkable page. Future safe zones can hook the same pattern. */}
        {!isLocked && region.id === "glass_spring_oasis" && (
          <a
            href="/oasis"
            data-testid="worldmap-enter-oasis"
            className="mt-5 inline-flex items-center justify-center w-full py-2.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-black font-black uppercase tracking-widest text-xs transition-colors"
          >
            🎣 Enter The Quiet Drop
          </a>
        )}
      </div>
    </div>
  );
}
